import React, { useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { BrandAsset } from '../../services/editingApi';
import type { EditList, Overlay, Position, TextOverlay } from '../../utils/editing/editList';
import { focusAt } from '../../utils/editing/editList';
import type { CueWord } from '../../utils/editing/captions';
import { spokenWordIndex } from '../../utils/editing/captions';

/**
 * What the renderer will draw, drawn over the preview instead: pictures, text
 * and captions at their times. This layer covers exactly the output frame (the
 * stage sizes it), and positions are shares of that frame, as the compiler
 * resolves them, so the preview and the export agree.
 *
 * When `editable`, pictures and text can be picked, dragged to a new place
 * (snapping to the centre lines) and resized from a corner handle.
 */

export interface PreviewCue {
  startMs: number;
  endMs: number;
  lines: string[];
  words?: CueWord[];
}

export type OverlaySelection = { kind: 'text' | 'overlay'; id: string } | null;

interface PreviewOverlayProps {
  list: EditList;
  outputMs: number;
  assets: BrandAsset[];
  cues: PreviewCue[];
  editable?: boolean;
  selected?: OverlaySelection;
  onSelect?: (selection: OverlaySelection) => void;
  /** A drag in progress (coalesced into one undo step by `key`). */
  onChange?: (next: EditList, key: string) => void;
}

const FONT_STACKS = {
  sans: '"Noto Sans", "Helvetica Neue", Arial, sans-serif',
  serif: '"Noto Serif", Georgia, serif',
  mono: '"Noto Sans Mono", Menlo, monospace',
};

const ASPECTS: Record<string, number> = { '16:9': 16 / 9, '9:16': 9 / 16, '1:1': 1, '4:5': 4 / 5 };

/** CSS for a position: anchored to an edge with a margin, or centred on a point. */
export function positionStyle(position: Position, frameHeightPx: number): React.CSSProperties {
  if (position.xPct !== null && position.yPct !== null) {
    return { left: `${position.xPct}%`, top: `${position.yPct}%`, transform: 'translate(-50%, -50%)' };
  }
  const margin = (position.marginPct / 100) * frameHeightPx;
  const style: React.CSSProperties = {};
  const a = position.anchor;
  if (a === 'top-left' || a === 'left' || a === 'bottom-left') style.left = margin;
  else if (a === 'top-right' || a === 'right' || a === 'bottom-right') style.right = margin;
  else { style.left = '50%'; style.transform = 'translateX(-50%)'; }
  if (a === 'top-left' || a === 'top' || a === 'top-right') style.top = margin;
  else if (a === 'bottom-left' || a === 'bottom' || a === 'bottom-right') style.bottom = margin;
  else {
    style.top = '50%';
    style.transform = style.transform ? 'translate(-50%, -50%)' : 'translateY(-50%)';
  }
  return style;
}

/** "#RRGGBBAA" → rgba(); "#RRGGBB" stays. */
export const cssColor = (hex: string | null): string | undefined => {
  if (!hex) return undefined;
  if (hex.length === 9) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const a = parseInt(hex.slice(7, 9), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${Math.round(a * 1000) / 1000})`;
  }
  return hex;
};

/** How far into its entrance (0→1) and exit (1→0) a text is: the smaller of the two. */
export const entranceProgress = (text: Pick<TextOverlay, 'fadeMs' | 'fromMs' | 'toMs'>, outputMs: number): number => {
  if (text.fadeMs <= 0) return 1;
  return Math.max(0, Math.min(1, (outputMs - text.fromMs) / text.fadeMs, (text.toMs - outputMs) / text.fadeMs));
};

/** The CSS the renderer's effect gives text without a box. */
export const textShadowFor = (effect: TextOverlay['effect'], sizePx: number): string => {
  if (effect === 'none') return 'none';
  if (effect === 'shadow') return `0 ${Math.max(1, sizePx * 0.06)}px ${Math.max(2, sizePx * 0.18)}px rgba(0,0,0,0.75)`;
  const o = Math.max(1, sizePx / 18);
  return `${o}px 0 0 #000, -${o}px 0 0 #000, 0 ${o}px 0 #000, 0 -${o}px 0 #000, ${o}px ${o}px 0 #000, -${o}px -${o}px 0 #000, ${o}px -${o}px 0 #000, -${o}px ${o}px 0 #000`;
};

/** The window a reframe keeps, as shares of the source frame (crop mode). */
/** The crop window as shares of the source frame, at a source time when the crop follows the person. */
export function reframeWindow(list: EditList, videoWidth: number, videoHeight: number, atMs?: number): { left: number; top: number; width: number; height: number } | null {
  if (list.output.aspect === 'source' || !videoWidth || !videoHeight || (list.reframe && list.reframe.mode === 'pad')) return null;
  const target = ASPECTS[list.output.aspect];
  const source = videoWidth / videoHeight;
  if (Math.abs(source - target) / target < 0.005) return null;
  const focus = list.reframe ? focusAt(list.reframe, atMs ?? 0) : { focusXPct: 50, focusYPct: 50 };
  const focusX = focus.focusXPct / 100;
  const focusY = focus.focusYPct / 100;
  let width = 1;
  let height = 1;
  if (source > target) width = target / source; else height = source / target;
  const left = Math.min(Math.max(focusX - width / 2, 0), 1 - width);
  const top = Math.min(Math.max(focusY - height / 2, 0), 1 - height);
  return { left, top, width, height };
}

/** The output frame's proportions (width / height). */
export function outputAspectRatio(list: EditList, videoWidth: number, videoHeight: number): number {
  if (list.output.aspect !== 'source') return ASPECTS[list.output.aspect];
  return videoWidth && videoHeight ? videoWidth / videoHeight : 16 / 9;
}

const SNAP_PCT = 2;
const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

interface Drag {
  kind: 'text' | 'overlay';
  id: string;
  mode: 'move' | 'resize';
  startX: number;
  startY: number;
  /** The element's centre at the start, as shares of the frame (%). */
  centreX: number;
  centreY: number;
  startSize: number;
  key: string;
}

const PreviewOverlay: React.FC<PreviewOverlayProps> = ({ list, outputMs, assets, cues, editable = false, selected = null, onSelect, onChange }) => {
  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const [guides, setGuides] = useState<{ x: boolean; y: boolean }>({ x: false, y: false });
  const dragRef = useRef<Drag | null>(null);
  const frameHeight = frameEl?.clientHeight || 0;
  const assetById = useMemo(() => {
    const map: Record<string, BrandAsset> = {};
    assets.forEach((asset) => { map[asset.id] = asset; });
    return map;
  }, [assets]);
  const cue = cues.find((c) => outputMs >= c.startMs && outputMs < c.endMs) || null;
  const captionStyle = list.captions?.style || null;
  const captionSize = captionStyle ? (captionStyle.sizePct / 100) * frameHeight : 0;

  const beginDrag = (event: React.PointerEvent, kind: 'text' | 'overlay', id: string, mode: 'move' | 'resize') => {
    if (!editable || !frameEl) return;
    event.stopPropagation();
    event.preventDefault();
    onSelect?.({ kind, id });
    const target = (mode === 'resize' ? (event.currentTarget.parentElement as HTMLElement) : (event.currentTarget as HTMLElement));
    const frame = frameEl.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    const item = kind === 'text' ? list.texts.find((t) => t.id === id) : list.overlays.find((o) => o.id === id);
    if (!item || frame.width === 0 || frame.height === 0) return;
    dragRef.current = {
      kind,
      id,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      centreX: ((box.left + box.width / 2 - frame.left) / frame.width) * 100,
      centreY: ((box.top + box.height / 2 - frame.top) / frame.height) * 100,
      startSize: kind === 'text' ? (item as TextOverlay).sizePct : (item as Overlay).widthPct,
      key: `${mode}-${kind}-${id}-${Date.now()}`,
    };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || !frameEl || !onChange) return;
    const frame = frameEl.getBoundingClientRect();
    const dx = ((event.clientX - drag.startX) / frame.width) * 100;
    const dy = ((event.clientY - drag.startY) / frame.height) * 100;
    if (Math.abs(event.clientX - drag.startX) < 2 && Math.abs(event.clientY - drag.startY) < 2) return;
    if (drag.mode === 'move') {
      let x = clamp(drag.centreX + dx, 0, 100);
      let y = clamp(drag.centreY + dy, 0, 100);
      const snapX = Math.abs(x - 50) < SNAP_PCT;
      const snapY = Math.abs(y - 50) < SNAP_PCT;
      if (snapX) x = 50;
      if (snapY) y = 50;
      setGuides({ x: snapX, y: snapY });
      const round = (v: number) => Math.round(v * 10) / 10;
      if (drag.kind === 'text') {
        onChange({ ...list, texts: list.texts.map((t) => (t.id === drag.id ? { ...t, position: { ...t.position, xPct: round(x), yPct: round(y) } } : t)) }, drag.key);
      } else {
        onChange({ ...list, overlays: list.overlays.map((o) => (o.id === drag.id ? { ...o, position: { ...o.position, xPct: round(x), yPct: round(y) } } : o)) }, drag.key);
      }
    } else if (drag.kind === 'overlay') {
      const widthPct = clamp(Math.round((drag.startSize + dx) * 10) / 10, 2, 100);
      onChange({ ...list, overlays: list.overlays.map((o) => (o.id === drag.id ? { ...o, widthPct } : o)) }, drag.key);
    } else {
      const sizePct = clamp(Math.round((drag.startSize + dy * 0.5) * 10) / 10, 1.5, 20);
      onChange({ ...list, texts: list.texts.map((t) => (t.id === drag.id ? { ...t, sizePct } : t)) }, drag.key);
    }
  };

  const endDrag = (event: React.PointerEvent) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setGuides({ x: false, y: false });
    (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
  };

  const handle = (kind: 'text' | 'overlay', id: string) => (
    <Box
      component="span"
      role="presentation"
      onPointerDown={(event: React.PointerEvent) => beginDrag(event, kind, id, 'resize')}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      sx={{ position: 'absolute', right: -7, bottom: -7, width: 14, height: 14, borderRadius: '3px', bgcolor: '#fff', border: '2px solid #2563EB', cursor: 'nwse-resize', pointerEvents: 'auto' }}
      data-testid={`resize-${kind}-${id}`}
    />
  );

  const selectedStyle = (kind: 'text' | 'overlay', id: string): React.CSSProperties => (
    editable && selected && selected.kind === kind && selected.id === id ? { outline: '2px solid #60A5FA', outlineOffset: 2 } : {}
  );

  return (
    <Box
      ref={setFrameEl}
      data-testid="output-frame"
      onPointerDown={() => { if (editable) onSelect?.(null); }}
      sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: editable ? 'auto' : 'none' }}
    >
      {list.overlays.map((overlay) => {
        const asset = assetById[overlay.assetId];
        const visible = (overlay.fromMs === null || outputMs >= overlay.fromMs) && (overlay.toMs === null || outputMs < overlay.toMs);
        if (!asset || !visible) return null;
        const isSelected = editable && selected?.kind === 'overlay' && selected.id === overlay.id;
        return (
          <Box
            key={overlay.id}
            data-testid={`preview-picture-${overlay.id}`}
            onPointerDown={(event: React.PointerEvent) => beginDrag(event, 'overlay', overlay.id, 'move')}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            sx={{ position: 'absolute', width: `${overlay.widthPct}%`, cursor: editable ? 'move' : 'default', touchAction: 'none', ...positionStyle(overlay.position, frameHeight), ...selectedStyle('overlay', overlay.id) }}
          >
            <img src={asset.url} alt="" draggable={false} data-testid={`preview-picture-img-${overlay.id}`} style={{ display: 'block', width: '100%', opacity: overlay.opacity, pointerEvents: 'none' }} />
            {isSelected ? handle('overlay', overlay.id) : null}
          </Box>
        );
      })}
      {list.texts.map((text) => {
        if (outputMs < text.fromMs || outputMs >= text.toMs) return null;
        const sizePx = (text.sizePct / 100) * frameHeight;
        const background = cssColor(text.backgroundColor);
        const progress = entranceProgress(text, outputMs);
        const entrance = text.entrance || 'fade';
        const base = positionStyle(text.position, frameHeight);
        const extra = entrance === 'slide' ? ` translateY(${(1 - progress) * 0.04 * frameHeight}px)` : entrance === 'pop' ? ` scale(${0.7 + 0.3 * Math.min(1, progress * 1.25)})` : '';
        const isSelected = editable && selected?.kind === 'text' && selected.id === text.id;
        const anchor = text.position.anchor;
        const align = text.position.xPct !== null ? 'center' : anchor === 'center' || anchor === 'top' || anchor === 'bottom' ? 'center' : anchor.endsWith('right') || anchor === 'right' ? 'right' : 'left';
        return (
          <Box
            key={text.id}
            data-testid={`preview-text-${text.id}`}
            onPointerDown={(event: React.PointerEvent) => beginDrag(event, 'text', text.id, 'move')}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            sx={{
              position: 'absolute',
              maxWidth: '86%',
              opacity: progress,
              fontFamily: FONT_STACKS[text.font],
              fontSize: sizePx,
              fontWeight: text.style === 'plain' ? 400 : 700,
              lineHeight: 1.15,
              color: text.color,
              textAlign: align,
              textShadow: background ? 'none' : textShadowFor(text.effect, sizePx),
              bgcolor: background,
              px: background ? `${sizePx * 0.35}px` : 0,
              py: background ? `${sizePx * 0.15}px` : 0,
              borderRadius: background ? `${sizePx * 0.12}px` : 0,
              whiteSpace: 'pre-wrap',
              cursor: editable ? 'move' : 'default',
              touchAction: 'none',
              userSelect: 'none',
              ...base,
              transform: `${base.transform || ''}${extra}`.trim() || undefined,
              ...selectedStyle('text', text.id),
            }}
          >
            {text.text}
            {text.secondaryText ? <Box component="span" sx={{ display: 'block', fontSize: sizePx * 0.66, fontWeight: 400 }}>{text.secondaryText}</Box> : null}
            {isSelected ? handle('text', text.id) : null}
          </Box>
        );
      })}
      {cue && captionStyle ? (
        <Box
          data-testid="preview-caption"
          sx={{
            position: 'absolute',
            left: '6%',
            right: '6%',
            textAlign: 'center',
            pointerEvents: 'none',
            ...(captionStyle.placement === 'top' ? { top: '5%' } : captionStyle.placement === 'middle' ? { top: '50%', transform: 'translateY(-50%)' } : { bottom: '5%' }),
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              fontFamily: FONT_STACKS[captionStyle.font],
              fontSize: captionSize,
              fontWeight: captionStyle.look === 'pop' ? 700 : 400,
              lineHeight: 1.25,
              color: captionStyle.color,
              bgcolor: cssColor(captionStyle.backgroundColor),
              textShadow: captionStyle.backgroundColor ? 'none' : textShadowFor('outline', captionSize),
              px: `${captionSize * 0.3}px`,
              py: `${captionSize * 0.1}px`,
              borderRadius: `${captionSize * 0.1}px`,
              whiteSpace: 'pre-line',
              transform: captionStyle.look === 'pop' ? `scale(${0.7 + 0.3 * Math.min(1, (outputMs - cue.startMs) / 90)})` : undefined,
            }}
          >
            {captionStyle.look === 'highlight' && cue.words && cue.words.length ? (
              <HighlightedCue cue={cue} outputMs={outputMs} color={captionStyle.highlightColor} />
            ) : cue.lines.join('\n')}
          </Box>
        </Box>
      ) : null}
      {guides.x ? <Box data-testid="guide-x" sx={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '1px', bgcolor: '#F472B6', pointerEvents: 'none' }} /> : null}
      {guides.y ? <Box data-testid="guide-y" sx={{ position: 'absolute', left: 0, right: 0, top: '50%', height: '1px', bgcolor: '#F472B6', pointerEvents: 'none' }} /> : null}
    </Box>
  );
};

/** A cue's lines with the word being said lit, as the renderer draws the highlight look. */
const HighlightedCue: React.FC<{ cue: PreviewCue; outputMs: number; color: string }> = ({ cue, outputMs, color }) => {
  const spoken = spokenWordIndex({ startMs: cue.startMs, endMs: cue.endMs, lines: cue.lines, words: cue.words }, outputMs);
  const lines: React.ReactNode[][] = [];
  (cue.words || []).forEach((word, index) => {
    const line = lines[word.line] || (lines[word.line] = []);
    if (line.length) line.push(' ');
    line.push(index === spoken ? <span key={index} data-testid="spoken-word" style={{ color }}>{word.text}</span> : <span key={index}>{word.text}</span>);
  });
  return (
    <>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {index > 0 ? '\n' : null}
          {line}
        </React.Fragment>
      ))}
    </>
  );
};

export default PreviewOverlay;
